import { z } from 'zod'
import type { LoggerPort } from '@omnea-app/packages/universal/general/observability/LoggerPort'

const logger: LoggerPort = console

/**
 * Use this function in z.catch() for schemas we're okay with them failing.
 *
 * For example, we're using this for form configs/responses, where historically the DB data has been inconsistent,
 * but we don't want lambdas to suddenly start failing just because of that.
 *
 * So instead we allow them to pass, but capture the error in Sentry.
 *
 * There's a ticket in the backlog to come back to these Sentry errors, clean the data and remove the catch.
 * @see https://linear.app/omnea/issue/COR-414
 *
 */
export const captureAndReturn =
  (schemaName: string) =>
  <Input, Output>(context: { error: { issues: z.core.$ZodIssue[] }; input: Input }): Output => {
    if (enableLogs) {
      logger.warn('Schema failed to parse - returning input anyway', {
        error: new Error(`Schema ${schemaName} failed to parse`),
        issues: context.error.issues.map(({ code, path }) => ({ code, path })),
      })
    }

    return context.input as unknown as Output
  }

/**
 * Wraps a schema with `.catch(captureAndReturn(...))` except during OpenAPI docs generation.
 * `zod-to-openapi` treats catch schemas as nullable (null parses successfully through the
 * catch), which emits a bogus empty `{nullable: true}` anyOf member that downstream
 * openapi-typescript consumers see as `unknown`, collapsing the whole union.
 */
export const withLenientParsing = <T extends z.ZodType>(schema: T, schemaName: string): T | z.ZodCatch<T> =>
  isDocsGeneration ? schema : schema.catch(captureAndReturn(schemaName))

export const isDocsGeneration = process.env.OPENAPI_DOCS_GENERATION === 'true'

// `zod-to-openapi` triggers `.parse` internally, we don't need to log these errors because they're not real errors
const enableLogs = process.env.SUPPRESS_ZOD_CATCH_LOGS !== 'true'
